/**
 * MyPodOps Studio V2 - Editor Integration
 *
 * Bridges Studio V2 multi-participant recordings with the
 * PodOps Studio content editor for post-production editing.
 *
 * Handles:
 * - Converting Studio V2 recording format to editor format
 * - Multi-track audio/video preparation
 * - Launching editor with recording context
 * - Syncing edits back to Studio V2 storage
 */

import { recordingsApi, Recording } from './api';

// Editor expects this format for tracks
export interface EditorTrack {
  id: string;
  name: string;
  type: 'audio' | 'video';
  color: string;
  url: string;
  duration?: number;
  volume: number;
  muted: boolean;
  participantId?: string;
  participantName?: string;
}

// Editor recording format
export interface EditorRecording {
  id: string;
  title: string;
  duration: number;
  createdAt: string;
  status: 'ready' | 'processing' | 'uploading';
  tracks: EditorTrack[];
  thumbnail?: string;
  metadata: {
    sessionId: string;
    hostId: string;
    hostName: string;
    participantCount: number;
    quality: string;
    hasVideo: boolean;
    hasAudio: boolean;
  };
}

// Track colors for multi-participant recordings
const TRACK_COLORS = [
  '#667eea', // Blue/Purple (Host)
  '#f56565', // Red (Guest 1)
  '#48bb78', // Green (Guest 2)
  '#ed8936', // Orange (Guest 3)
  '#9f7aea', // Purple (Guest 4)
  '#38b2ac', // Teal (Guest 5)
  '#fc8181', // Light Red (Guest 6)
  '#68d391', // Light Green (Guest 7)
  '#f6ad55', // Light Orange (Guest 8)
  '#b794f4', // Light Purple (Guest 9)
];

/**
 * Convert Studio V2 recording to editor format
 */
export function convertToEditorFormat(
  recording: Recording,
  downloadUrls: Record<string, string>
): EditorRecording {
  const tracks: EditorTrack[] = [];
  let trackIndex = 0;

  // Parse the recording data
  // Studio V2 stores participant recordings with keys like:
  // - video_{participantId}
  // - audio_{participantId}
  // - screen_{participantId}

  // Get unique participants from s3Key patterns
  const participantPattern = /\/(video|audio|screen)_([^\/]+)\//;
  const participants = new Map<string, { video?: string; audio?: string; screen?: string }>();

  Object.entries(downloadUrls).forEach(([key, url]) => {
    const match = key.match(participantPattern) || url.match(participantPattern);
    if (match) {
      const [, mediaType, participantId] = match;
      if (!participants.has(participantId)) {
        participants.set(participantId, {});
      }
      participants.get(participantId)![mediaType as 'video' | 'audio' | 'screen'] = url;
    }
  });

  // If no participant pattern found, treat as single track
  if (participants.size === 0 && downloadUrls.main) {
    tracks.push({
      id: 'track-main',
      name: recording.participantName || 'Main Recording',
      type: recording.mediaType === 'video' ? 'video' : 'audio',
      color: TRACK_COLORS[0],
      url: downloadUrls.main,
      duration: recording.duration,
      volume: 1.0,
      muted: false,
      participantId: recording.participantId,
      participantName: recording.participantName,
    });
  } else {
    // Create tracks for each participant
    participants.forEach((media, participantId) => {
      const color = TRACK_COLORS[trackIndex % TRACK_COLORS.length];
      const isHost = trackIndex === 0;

      // Add video track if exists
      if (media.video) {
        tracks.push({
          id: `track-video-${participantId}`,
          name: isHost ? `Host Video` : `Guest ${trackIndex} Video`,
          type: 'video',
          color,
          url: media.video,
          volume: 1.0,
          muted: false,
          participantId,
          participantName: isHost ? 'Host' : `Guest ${trackIndex}`,
        });
      }

      // Add audio track if exists
      if (media.audio) {
        tracks.push({
          id: `track-audio-${participantId}`,
          name: isHost ? `Host Audio` : `Guest ${trackIndex} Audio`,
          type: 'audio',
          color,
          url: media.audio,
          volume: 1.0,
          muted: false,
          participantId,
          participantName: isHost ? 'Host' : `Guest ${trackIndex}`,
        });
      }

      // Add screen share track if exists
      if (media.screen) {
        tracks.push({
          id: `track-screen-${participantId}`,
          name: isHost ? `Host Screen` : `Guest ${trackIndex} Screen`,
          type: 'video',
          color: '#718096', // Gray for screen shares
          url: media.screen,
          volume: 1.0,
          muted: true, // Screen shares usually don't have audio
          participantId,
          participantName: isHost ? 'Host' : `Guest ${trackIndex}`,
        });
      }

      trackIndex++;
    });
  }

  return {
    id: recording.recordingId,
    title: recording.sessionName || 'Untitled Recording',
    duration: recording.duration || 0,
    createdAt: recording.createdAt,
    status: recording.status === 'ready' ? 'ready' : 'processing',
    tracks,
    thumbnail: undefined, // Could be generated from first video frame
    metadata: {
      sessionId: recording.sessionId,
      hostId: recording.hostId,
      hostName: recording.participantName || 'Host',
      participantCount: participants.size || 1,
      quality: recording.quality,
      hasVideo: tracks.some(t => t.type === 'video'),
      hasAudio: tracks.some(t => t.type === 'audio'),
    },
  };
}

/**
 * Open the PodOps Studio editor with a Studio V2 recording
 */
export async function openInEditor(recordingId: string): Promise<void> {
  // Get recording details with download URL
  const recording = await recordingsApi.get(recordingId);

  if (!recording.downloadUrl) {
    throw new Error('Recording not ready for editing');
  }

  // Build editor URL with Studio V2 source flag
  const editorUrl = new URL('/studio/editor', window.location.origin);
  editorUrl.searchParams.set('id', recordingId);
  editorUrl.searchParams.set('source', 'studio-v2');

  // Navigate to editor
  window.location.href = editorUrl.toString();
}

/**
 * Open editor in a new window/tab
 */
export function openInEditorNewWindow(recordingId: string): Window | null {
  const editorUrl = new URL('/studio/editor', window.location.origin);
  editorUrl.searchParams.set('id', recordingId);
  editorUrl.searchParams.set('source', 'studio-v2');

  return window.open(editorUrl.toString(), '_blank');
}

/**
 * Get editor-compatible recording data via API
 * This can be used by the editor to fetch Studio V2 recordings
 */
export async function getEditorRecording(recordingId: string): Promise<EditorRecording> {
  const recording = await recordingsApi.get(recordingId);

  if (!recording.downloadUrl) {
    throw new Error('Recording not ready for editing');
  }

  // Create download URLs map
  const downloadUrls: Record<string, string> = {
    main: recording.downloadUrl,
  };

  return convertToEditorFormat(recording, downloadUrls);
}

/**
 * List all Studio V2 recordings in editor-compatible format
 */
export async function listEditorRecordings(params?: {
  hostId?: string;
  status?: string;
  limit?: number;
}): Promise<{
  recordings: EditorRecording[];
  nextKey?: string;
}> {
  const result = await recordingsApi.list({
    hostId: params?.hostId,
    status: params?.status,
    limit: params?.limit,
  });

  // Convert each recording (without download URLs for listing)
  const editorRecordings: EditorRecording[] = result.recordings.map(rec => ({
    id: rec.recordingId,
    title: rec.sessionName || 'Untitled Recording',
    duration: rec.duration || 0,
    createdAt: rec.createdAt,
    status: rec.status === 'ready' ? 'ready' : 'processing',
    tracks: [], // Tracks loaded when opening in editor
    metadata: {
      sessionId: rec.sessionId,
      hostId: rec.hostId,
      hostName: rec.participantName || 'Host',
      participantCount: 1, // Updated when fully loaded
      quality: rec.quality,
      hasVideo: rec.mediaType === 'video',
      hasAudio: true,
    },
  }));

  return {
    recordings: editorRecordings,
    nextKey: result.nextKey,
  };
}

/**
 * EditorBridge - Singleton for editor communication
 */
export class EditorBridge {
  private static instance: EditorBridge;
  private editorWindow: Window | null = null;
  private messageHandlers: Map<string, (data: any) => void> = new Map();

  private constructor() {
    // Listen for messages from editor
    window.addEventListener('message', this.handleMessage.bind(this));
  }

  static getInstance(): EditorBridge {
    if (!EditorBridge.instance) {
      EditorBridge.instance = new EditorBridge();
    }
    return EditorBridge.instance;
  }

  /**
   * Open editor with recording and establish communication
   */
  async openEditor(recordingId: string): Promise<void> {
    // Close existing editor if open
    if (this.editorWindow && !this.editorWindow.closed) {
      this.editorWindow.focus();
      // Send new recording to existing editor
      this.sendToEditor({
        type: 'load-recording',
        recordingId,
        source: 'studio-v2',
      });
      return;
    }

    // Open new editor window
    this.editorWindow = openInEditorNewWindow(recordingId);

    // Wait for editor to be ready
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Editor failed to load'));
      }, 30000);

      this.onMessage('editor-ready', () => {
        clearTimeout(timeout);
        resolve();
      });
    });
  }

  /**
   * Send message to editor
   */
  sendToEditor(data: { type: string; [key: string]: any }): void {
    if (this.editorWindow && !this.editorWindow.closed) {
      this.editorWindow.postMessage(
        { ...data, source: 'studio-v2' },
        window.location.origin
      );
    }
  }

  /**
   * Register message handler
   */
  onMessage(type: string, handler: (data: any) => void): void {
    this.messageHandlers.set(type, handler);
  }

  /**
   * Handle incoming messages from editor
   */
  private handleMessage(event: MessageEvent): void {
    // Verify origin
    if (event.origin !== window.location.origin) return;

    const { type, ...data } = event.data || {};

    // Handle known message types
    const handler = this.messageHandlers.get(type);
    if (handler) {
      handler(data);
    }

    // Built-in handlers
    switch (type) {
      case 'editor-ready':
        console.log('[EditorBridge] Editor is ready');
        break;

      case 'edit-saved':
        console.log('[EditorBridge] Edit saved:', data);
        break;

      case 'export-complete':
        console.log('[EditorBridge] Export complete:', data);
        break;

      case 'editor-closed':
        console.log('[EditorBridge] Editor closed');
        this.editorWindow = null;
        break;
    }
  }

  /**
   * Check if editor is open
   */
  isEditorOpen(): boolean {
    return this.editorWindow !== null && !this.editorWindow.closed;
  }

  /**
   * Close editor
   */
  closeEditor(): void {
    if (this.editorWindow && !this.editorWindow.closed) {
      this.editorWindow.close();
    }
    this.editorWindow = null;
  }
}

// Export singleton instance
export const editorBridge = EditorBridge.getInstance();

// Export default
export default editorBridge;
